import {Elements} from '@stripe/react-stripe-js';
import {PaymentRequest, Stripe, StripeElementsOptions} from '@stripe/stripe-js';
import React, {useEffect, useRef, useState} from 'react';
import {Helmet} from 'react-helmet';
import {useHistory} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import * as Sentry from '@sentry/react';
import {BottomSheet} from '../../components/BottomSheet';
import MaybeStagingBadge from '../../components/MaybeStagingBadge';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import PaymentIntentScreen from '../../screens/StripeCustomCheckout/PaymentIntentScreen';
import usePaywallDismissTracking from '../../hooks/usePaywallDismissTracking';
import usePaywallAnalytics from '../../hooks/usePaywallAnalytics';
import usePaywallScrollDepth from '../../hooks/usePaywallScrollDepth';
import {PaywallSlug} from '../../analytics/paywallEvents';
import Analytics from '../../utils/Analytics';
import Environment from '../../utils/Environment';
import {useQueryToken, useValueParams} from '../../utils/dynamicCodes/hooks';
import {reportRepeatCheckout} from '../../utils/dynamicCodes/redirects';
import TrialDetailsList from '../../projects/emailConfirmation-0424/components/TrialDetailsList';
import TrialSelectionCells from '../../projects/emailConfirmation-0424/components/TrialSelectionCells';
import {
  createCheckoutDetails,
  createPaymentRequest,
  createStripeInstance,
} from '../../projects/emailConfirmation-0424/setupStripeForEmailConfirmationPaywall';
import {getEmailConfirmationProducts, ProductCode} from '../../projects/emailConfirmation-0424/products';
import {allowedLocale} from '../../projects/emailConfirmation-0424/screens/EmailConfirmationCheckoutScreen';
import '../../projects/emailConfirmation-0424/screens/EmailConfirmation.css';
import '../I03-name-personalization/videoBackground.css';
import '../I03-name-personalization/screens/NameGreetingScreen.css';
import './SocialProofBelowFoldScreen.css';
import SocialProofBelowFold, {SocialProofBelowFoldVariant} from './SocialProofBelowFold';
import ScrollNudge from './ScrollNudge';
import validateEmail from '../../utils/validateEmail';
import {growthBook} from '../../index';
import {sanitizeFirstName} from '../I03-name-personalization/sanitizeFirstName';
import {getSecondChanceOffer} from '../../second-chance-offer/secondChanceOfferPrices';
import {useDismissTreatment, useDismissTreatmentConfig} from '../../second-chance-offer/useDismissTreatment';
import DismissCloseButton from '../../second-chance-offer/DismissCloseButton';
import {getSearchParams} from '../../utils/urlQuery';
import {tokenParam} from '../../utils/linkOutSession';

/**
 * I36 — social proof BELOW the plan grid on the link-out paywall.
 *
 * Forked from NameGreetingScreen, which is production for this cohort (US / lang=en /
 * Apple Pay / non-student / 18+). Everything above the CTA is unchanged; the only delta is
 * a credibility block appended under the fold, plus scroll instrumentation to measure who
 * ever reaches it.
 *
 * This is the follow-up the April 2026 /social-proof result asked for. That test put the
 * same material in a full interstitial in FRONT of the plan grid and lost 7.7% of
 * non-student and 9.4% of student trial starts — to the extra step, per its own reading,
 * not to the message. Here there is no step: a user who never scrolls sees production.
 *
 * Arms are the two `variant` values; see SocialProofBelowFold for what each renders.
 */
const ARM_CONFIG: Record<SocialProofBelowFoldVariant, {paywall: PaywallSlug; variantName: string}> = {
  quiet: {paywall: 'press-badges', variantName: 'i36_quiet'},
  full: {paywall: 'social-proof-scroll', variantName: 'i36_full'},
};

export type SocialProofBelowFoldScreenProps = {
  variant: SocialProofBelowFoldVariant;
  initialOption: ProductCode;
  otherOption: ProductCode;
  isStudent: boolean;
};

const SocialProofBelowFoldScreen = (props: SocialProofBelowFoldScreenProps) => {
  useTranslation();
  const {variant, initialOption, otherOption, isStudent} = props;
  const {paywall: PAYWALL, variantName: VARIANT_NAME} = ARM_CONFIG[variant];
  useDocumentTitle('Rise');
  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
  const token = useQueryToken();
  const localeParam: string = useValueParams('locale') ?? 'US';
  const locale = allowedLocale(localeParam);
  const firstName = sanitizeFirstName(useValueParams('firstName'));
  const history = useHistory();
  const [trialOption, setTrialOption] = useState<ProductCode>(initialOption);

  const reminderDaysBeforeParam = useValueParams('reminderDaysBefore');
  const reminderDaysBefore = reminderDaysBeforeParam ? parseInt(reminderDaysBeforeParam, 10) : undefined;

  const emailConfirmationProducts = getEmailConfirmationProducts();
  const product = emailConfirmationProducts[trialOption][locale];

  const [options, setOptions] = useState<StripeElementsOptions>({});
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | undefined>(undefined);
  const [paymentRequestLoading, setPaymentRequestLoading] = useState<boolean>(true);
  const [clientSecret, setClientSecret] = useState<string>('');
  const [duplicateCustomer, setDuplicateCustomer] = useState<boolean>(false);
  const [chargeDate, setChargeDate] = useState<string>('');
  const [prepaidBlockMode, setPrepaidBlockMode] = useState<'disallow_try_again' | 'disallow_and_redirect' | null>(null);
  const [videoReady, setVideoReady] = useState<boolean>(false);

  // I21 — explicit dismiss treatment (GrowthBook `web_payments_dismiss_treatment`, with
  // ?dismissTreatment= as a QA override). Runs once the payment UI is ready, with or without
  // a wallet: the offer sheet takes Apple/Google Pay or a card.
  const secondChanceOffer = getSecondChanceOffer(locale, isStudent);
  const openSecondChanceRef = useRef<((source?: string) => void) | undefined>(undefined);
  const paymentReady = Boolean(stripeInstance) && Object.keys(options).length > 0 && !paymentRequestLoading;
  const {variant: dismissVariant, revealMs, idleMs, configReady} = useDismissTreatmentConfig();
  const {showCloseButton, closeButtonVisible, triggerDismiss, onCheckoutStarted} = useDismissTreatment({
    variant: dismissVariant,
    ready: paymentReady,
    paywall: PAYWALL,
    configReady,
    revealMs,
    idleMs,
    openOffer: source => openSecondChanceRef.current?.(source),
  });

  // Standard paywall funnel events. Dual-fires the legacy I03 names (shared across the four
  // I03 screens) so existing dashboards keep receiving data until they're rebuilt.
  // No `legacy` block: these are new slugs with no existing Amplitude charts to keep alive.
  const paywallAnalytics = usePaywallAnalytics({
    paywall: PAYWALL,
    baseProps: {email: token, locale, isStudent, variant: VARIANT_NAME, hasFirstName: !!firstName},
  });

  // The diagnostic this whole card turns on: how far down people actually go, and whether
  // they ever reach the block. An arm whose block nobody sees can't have moved anything.
  const {observeSection} = usePaywallScrollDepth({
    paywall: PAYWALL,
    properties: {email: token, locale, isStudent, variant: VARIANT_NAME, trialOption},
  });

  // Paywall dismiss / dwell + refocus instrumentation (B08 / I21). `markPurchased` is called
  // on success below so the post-purchase navigation isn't counted as a dismiss. Dwell is
  // especially worth watching here — a below-fold block that holds attention without
  // converting shows up as longer dwell with flat trial starts.
  const {markPurchased} = usePaywallDismissTracking({
    paywall: PAYWALL,
    properties: {
      email: token,
      locale,
      isStudent,
      variant: VARIANT_NAME,
      hasFirstName: !!firstName,
      trialOption,
      code: product.code,
    },
  });

  useEffect(() => {
    paywallAnalytics.viewed();
  }, [token, locale, isStudent, firstName, paywallAnalytics]);

  useEffect(() => {
    const setupStripe = async () => {
      if (token && !stripeInstance) {
        const checkoutDetails = await createCheckoutDetails(token);
        if ('error' in checkoutDetails) {
          if (checkoutDetails.duplicateCustomer) {
            setDuplicateCustomer(true);
          } else {
            Analytics.track('Unable to set up Stripe Custom Checkout Page', {
              env: Environment.getVar('REACT_APP_ENV') ?? 'test',
              email: token,
            });
          }
        } else {
          const stripe = await createStripeInstance();
          if (stripe) {
            setStripeInstance(stripe);
          }
          setClientSecret(checkoutDetails.client_secret);
          setChargeDate(checkoutDetails.charge_date);
          setOptions(checkoutDetails.options);
        }
      }
    };
    if (token) {
      setupStripe();
    }
  }, [stripeInstance, token]);

  useEffect(() => {
    if (duplicateCustomer) {
      reportRepeatCheckout(product.code, token);
    }
  }, [duplicateCustomer, product.code, token]);

  useEffect(() => {
    if (stripeInstance) {
      if (paymentRequest) {
        paymentRequest.update({
          total: {label: product.sheetLabel, amount: product.price},
        });
      } else {
        createPaymentRequest(stripeInstance, product.code, product.price, product.sheetLabel, locale, PAYWALL).then(
          paymentRequest => {
            setPaymentRequest(paymentRequest);
            setPaymentRequestLoading(false);
          },
        );
      }
    }
  }, [paymentRequest, product.code, product.price, product.sheetLabel, stripeInstance, trialOption, locale, PAYWALL]);

  const handleTrialSelection = (option: ProductCode) => {
    paywallAnalytics.trialSelection({trialOption: option});
    setTrialOption(option);
  };

  const handlePurchaseSuccess = ({existingAccount: _existingAccount}: {existingAccount: boolean}) => {
    markPurchased();
    paywallAnalytics.purchaseSuccess({trialOption});
    const reminderParam = reminderDaysBefore ? `&reminderDaysBefore=${reminderDaysBefore}` : '';
    history.push(`/email-confirmation-trial?${tokenParam(token)}days=${product.daysFree}${reminderParam}`);
  };

  // Restore the native pay sheet to the selected trial option after a second-chance
  // re-show is dismissed without purchase (the accept handler mutated it to the offer).
  const handleSecondChanceDismissed = () => {
    if (paymentRequest) {
      paymentRequest.update({total: {label: product.sheetLabel, amount: product.price}});
    }
  };

  const handleSecondChanceSuccess = () => {
    markPurchased();
    // Offer purchases also fire the standard Paywall Purchase Success (secondChance: true) so
    // purchase funnels count both arms; PaymentIntentScreen adds Dismiss Offer Purchase Success.
    paywallAnalytics.purchaseSuccess({trialOption, secondChance: true, offerCode: secondChanceOffer.code});
    const reminderParam = reminderDaysBefore ? `&reminderDaysBefore=${reminderDaysBefore}` : '';
    history.push(`/email-confirmation-trial?${tokenParam(token)}days=${secondChanceOffer.daysFree}${reminderParam}`);
  };

  const refToScroll = useRef<HTMLDivElement>(null);
  const onCreditCardChoice = () => {
    refToScroll.current?.scrollIntoView({behavior: 'smooth'});
  };

  // Return affordance for the states with no sticky bar. Scrolls to the same 25vh anchor the
  // credit-card reveal uses, which brings the plan grid and CTA back into view.
  const handleReturnToPlans = (source: 'back_to_plans') => {
    Analytics.track('I36 Return To Plans Clicked', {
      env: Environment.getVar('REACT_APP_ENV') ?? 'test',
      email: token,
      paywall: PAYWALL,
      variant: VARIANT_NAME,
      trialOption,
      source,
    });
    refToScroll.current?.scrollIntoView({behavior: 'smooth'});
  };

  // --- Sticky CTA plumbing (full arm only) ------------------------------------------------
  //
  // The sticky bar is offered only when the paywall's live CTA is our own
  // CustomPaymentRequestButton, because that is the one case where the bar can genuinely BE
  // the CTA: it renders that same component with this same paymentRequest, so it looks
  // identical and opens the same wallet sheet. PaymentIntentScreen also renders Stripe's own
  // branded button and, with no wallet, a credit-card form — neither can be reproduced out
  // here, and a bar captioned "TRY FOR $0.00" next to either would advertise an action it
  // can't perform.
  const paymentAreaRef = useRef<HTMLDivElement>(null);
  const [liveCtaOffScreen, setLiveCtaOffScreen] = useState(false);
  // Mirrors PaymentIntentScreen's own QA hatch so the bar can be checked on builds with no
  // wallet (headless Chrome has no Payment Request API at all). Non-production only, same as
  // the flag it shadows; the forwarded click is a no-op there because there is no real
  // payment request behind the button.
  const qaForceCta =
    !Environment.isProduction && typeof window !== 'undefined' && getSearchParams().get('qaForceCTA') === '1';
  // No payment area means no button to forward to — e.g. Stripe setup failed, or the user is
  // already subscribed. The bar must not advertise a purchase that cannot start, so in those
  // states the block falls back to "Back to plans". Same condition the payment block renders on.
  const paymentAreaMounted = !duplicateCustomer && Object.keys(options).length > 0 && !!stripeInstance;
  const usesOwnWalletCta =
    (!!paymentRequest && growthBook.getFeatureValue('paywall_link_out_custom_cta', false)) || qaForceCta;
  const showStickyCta = variant === 'full' && paymentAreaMounted && usesOwnWalletCta;

  // Show the bar strictly once the live CTA has left the viewport. A scroll-offset guess
  // leaves a window with two purchase buttons on screen at the same time.
  useEffect(() => {
    const node = paymentAreaRef.current;
    if (!showStickyCta || !node || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => setLiveCtaOffScreen(!entry.isIntersecting), {threshold: 0});
    observer.observe(node);
    return () => observer.disconnect();
  }, [showStickyCta, stripeInstance, options]);

  // Analytics only — the button opens the wallet sheet itself. 'Custom CTA Clicked' is the
  // same event the paywall's own CTA fires, so a purchase started from down here lands in
  // the existing funnel rather than looking like a drop-off.
  // --- Scroll hint -------------------------------------------------------------------------
  //
  // The block is worth nothing to a user who never learns it is there, and this paywall
  // gives no reason to scroll. The chevron overlays whatever is beneath it and is inert, so
  // it can never swallow a tap on the CTA — see ScrollNudge. Identical in both arms, so
  // quiet-vs-full still isolates the content.
  const handleNudgeShown = () =>
    Analytics.track('I36 Scroll Nudge Shown', {
      env: Environment.getVar('REACT_APP_ENV') ?? 'test',
      email: token,
      paywall: PAYWALL,
      variant: VARIANT_NAME,
      trialOption,
    });

  const handleStickyCtaClick = () => {
    // The sticky button opens the wallet too, so it must stop the idle-time timer.
    onCheckoutStarted();
    Analytics.track('Custom CTA Clicked');
    Analytics.track('I36 Sticky CTA Clicked', {
      env: Environment.getVar('REACT_APP_ENV') ?? 'test',
      email: token,
      paywall: PAYWALL,
      variant: VARIANT_NAME,
      trialOption,
    });
  };

  const handlePrepaidDetected = (mode: 'disallow_try_again' | 'disallow_and_redirect') => {
    setPrepaidBlockMode(mode);
    Analytics.track('Card Not Supported Sheet Viewed', {
      code: product.code,
      env: Environment.getVar('REACT_APP_ENV') ?? 'test',
      email: token,
      mode,
    });
  };

  useEffect(() => {
    const tokenIsLikelyAnId = token && !validateEmail(token);
    if (token && tokenIsLikelyAnId) {
      try {
        Analytics.identify(Number(token));
        Analytics.track('Identified user via token', {token: Number(token)});
      } catch (error) {
        Analytics.track('Unable to identify user via token', {token});
        Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
      }
    }
  }, [token]);

  return (
    <>
      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
      </Helmet>
      <div className="w-full mx-auto checkout-screen-background i03-name-greeting-screen i36-social-proof-screen">
        {/* I21 dismiss affordance — consistently top-right across paywalls. */}
        {showCloseButton ? <DismissCloseButton visible={closeButtonVisible} onClick={triggerDismiss} /> : null}
        <div className="i03-video-placeholder" aria-hidden="true" />
        <video
          autoPlay
          muted
          playsInline
          loop={true}
          id="Rise"
          className="fixed top-0 left-0 w-full object-cover object-top i03-video-fade"
          style={{marginTop: '-5vh', opacity: videoReady ? 1 : 0}}
          onCanPlay={() => setVideoReady(true)}
        >
          <source
            src="https://nyx-content-6xgk79jynn.s3.amazonaws.com/onboarding/paywall-v1b-edit-1290x2796-compressed-fast.mp4"
            type="video/mp4"
          />
          Your browser does not support HTML5 video.
        </video>

        <div
          ref={refToScroll}
          style={{position: 'absolute', left: 0, top: '25vh', backgroundColor: 'transparent', height: 1, width: '100%'}}
        />
        <div className="checkout-screen-content">
          <div className="i36-checkout-fill w-full">
            <div className="pt-2 flex flex-col i03-name-greeting-stack">
              {firstName ? (
                <div
                  style={{
                    color: '#fff',
                    padding: '0 24px 0 12px',
                    fontSize: 15,
                    fontWeight: 600,
                  }}
                >
                  One last step, {firstName} - your plan's ready.
                </div>
              ) : null}
              <TrialSelectionCells
                onSelect={handleTrialSelection}
                selection={trialOption}
                products={[
                  getEmailConfirmationProducts()[initialOption][locale],
                  getEmailConfirmationProducts()[otherOption][locale],
                ]}
                isStudent={isStudent}
              />
              <div style={{paddingTop: 12}}>
                <TrialDetailsList trialOption={getEmailConfirmationProducts()[trialOption][locale]} />
              </div>
              <div>
                {duplicateCustomer ? (
                  <div className="text-white">Your subscription is active!</div>
                ) : Object.keys(options).length && stripeInstance ? (
                  <div
                    ref={paymentAreaRef}
                    className={growthBook.getFeatureValue('paywall_link_out_custom_cta', false) ? '' : 'pl-5 pr-5'}
                    style={
                      growthBook.getFeatureValue('paywall_link_out_custom_cta', false)
                        ? {marginLeft: 12, marginRight: 12}
                        : {}
                    }
                  >
                    <Elements stripe={stripeInstance} options={options}>
                      <PaymentIntentScreen
                        code={product.code}
                        paywall={PAYWALL}
                        paymentRequest={paymentRequest}
                        clientSecret={clientSecret}
                        daysFree={product.daysFree}
                        chargeDate={chargeDate}
                        dynamicCheckout={true}
                        onPurchaseSuccess={handlePurchaseSuccess}
                        onPrepaidDetected={handlePrepaidDetected}
                        paymentRequestLoading={paymentRequestLoading}
                        onCreditCardChoice={onCreditCardChoice}
                        ctaText={product.daysFree === 7 ? 'REDEEM 7 DAYS FREE' : `REDEEM ${product.daysFree} DAYS`}
                        useUpdatedCreditCardLogic={true}
                        useCustomCTA={true}
                        customCTAText={product.daysFree === 7 ? 'TRY FOR $0.00' : 'TRY FOR A MONTH'}
                        secondChanceOffer={secondChanceOffer}
                        onSecondChanceSuccess={handleSecondChanceSuccess}
                        onSecondChanceDismissed={handleSecondChanceDismissed}
                        secondChanceOpenerRef={openSecondChanceRef}
                        onCheckoutStarted={onCheckoutStarted}
                      />
                    </Elements>
                  </div>
                ) : null}
              </div>
              {/* Help relocated from the top-right corner (now the ✕'s spot) to a low-key
                  bottom link — same as /name-greeting. */}
              <div style={{textAlign: 'center', padding: '16px 0 24px'}}>
                <a
                  href="https://risesciencehelp.zendesk.com/hc/en-us"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity"
                  style={{color: 'rgba(255,255,255,0.6)', fontSize: 13, textDecoration: 'underline'}}
                  onClick={() =>
                    Analytics.track('Help Clicked', {
                      env: Environment.getVar('REACT_APP_ENV') ?? 'test',
                      email: token,
                      variant: VARIANT_NAME,
                    })
                  }
                >
                  Need help?
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* ===== Everything above this line is /name-greeting, unchanged ===== */}
        <SocialProofBelowFold
          variant={variant}
          onReturnToPlans={handleReturnToPlans}
          stickyCta={
            showStickyCta
              ? {
                  // Same expression PaymentIntentScreen is given for customCTAText, so the
                  // bar and the live CTA cannot drift apart.
                  text: product.daysFree === 7 ? 'TRY FOR $0.00' : 'TRY FOR A MONTH',
                  subtext: product.pricingText,
                  visible: liveCtaOffScreen,
                  paymentRequest,
                  onClick: handleStickyCtaClick,
                }
              : undefined
          }
          sectionRef={observeSection('social-proof')}
        />

        <ScrollNudge onShown={handleNudgeShown} />

        <BottomSheet
          isOpen={prepaidBlockMode !== null}
          onClose={() => {
            Analytics.track('Card Not Supported Sheet Dismissed', {
              code: product.code,
              env: Environment.getVar('REACT_APP_ENV') ?? 'test',
              email: token,
              mode: prepaidBlockMode,
            });
            setPrepaidBlockMode(null);
          }}
        >
          <div className="flex flex-col items-center text-center px-2 pb-4">
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 16,
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="5" width="20" height="14" rx="2" stroke="#999" strokeWidth="2" />
                <line x1="2" y1="10" x2="22" y2="10" stroke="#999" strokeWidth="2" />
              </svg>
            </div>
            <h3 className="text-white text-xl font-bold mb-2">This card isn't supported</h3>
            {prepaidBlockMode === 'disallow_try_again' ? (
              <>
                <p className="text-gray-400 text-sm mb-6" style={{maxWidth: 280}}>
                  We weren't able to process this card. Please try a different card from your wallet.
                </p>
                <button
                  className="btn max-w-full w-full"
                  style={{height: 52, borderRadius: 12, fontSize: 16, fontWeight: 600}}
                  onClick={() => {
                    Analytics.track('Prepaid Try Different Card', {
                      code: product.code,
                      env: Environment.getVar('REACT_APP_ENV') ?? 'test',
                      email: token,
                    });
                    setPrepaidBlockMode(null);
                  }}
                >
                  Try a Different Card
                </button>
              </>
            ) : (
              <>
                <p className="text-gray-400 text-sm mb-6" style={{maxWidth: 280}}>
                  We weren't able to process this card. Finish setup in the app.
                </p>
                <a
                  href="nyx://"
                  onClick={() =>
                    Analytics.track('Prepaid Routed to App', {
                      code: product.code,
                      env: Environment.getVar('REACT_APP_ENV') ?? 'test',
                      email: token,
                    })
                  }
                  className="btn max-w-full w-full flex items-center justify-center gap-2"
                  style={{height: 52, borderRadius: 12, fontSize: 16, fontWeight: 600, textDecoration: 'none'}}
                >
                  Continue in the App
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{marginLeft: 4}}>
                    <path
                      d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </a>
              </>
            )}
          </div>
        </BottomSheet>

        <MaybeStagingBadge />
      </div>
    </>
  );
};

export default SocialProofBelowFoldScreen;
